import React, { useState, useEffect, useRef } from 'react';
import { 
  Table, Button, Card, Space, Modal, Typography, Tag, 
  Tooltip, Row, Col, Input, Spin, Descriptions, Divider,
  Badge, Tabs, Empty, Avatar, List, message, Radio, Drawer, DatePicker
} from 'antd';
import { 
  SearchOutlined, TeamOutlined, SyncOutlined,
  InfoCircleOutlined, UserOutlined, PlayCircleOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import api from '../services/api';
import groupService from '../services/groupService';
import SopExecutionRecords from '../components/SopExecutionRecords';
import EmployeeSelect from '../components/common/EmployeeSelect';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

// 群组相关的本地服务方法
const localGroupService = {
  syncGroups: async () => {
    // 从localStorage获取tenant对象
    const tenantStr = localStorage.getItem('tenant');
    let tenantId;
    
    try {
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        tenantId = tenant.id.toString();
      } else {
        throw new Error('找不到租户信息');
      }
    } catch (error) {
      console.error('解析tenant信息失败:', error);
      throw new Error('租户信息解析失败，无法继续操作');
    }
    
    const sourceType = 'WEWORK'; // 企业微信类型
    console.log('同步群组参数:', { tenantId, sourceType });
    return await api.post(`/sync/groups/async`, null, {
      params: { tenantId, sourceType }
    });
  },

  getSopTemplates: async () => {
    try {
      // 尝试获取活跃状态的SOP模板，添加targetType=GROUP参数
      const response = await api.get('/v1/sop/templates', {
        params: { status: 'active', deleted: false, targetType: 'GROUP' }
      });
      console.log('获取SOP模板响应:', response);
      return response.data;
    } catch (error) {
      console.error('获取SOP模板列表错误:', error);
      // 如果指定参数的请求失败，尝试不带参数的请求，但在前端过滤
      try {
        const fallbackResponse = await api.get('/v1/sop/templates');
        console.log('备选SOP模板请求成功:', fallbackResponse.data);
        // 如果后端不支持targetType参数过滤，前端手动过滤
        let result = fallbackResponse.data;
        if (Array.isArray(result)) {
          return result.filter(template => template.targetType === 'GROUP');
        } else if (result && Array.isArray(result.content)) {
          result.content = result.content.filter(template => template.targetType === 'GROUP');
          return result;
        }
        return result;
      } catch (fallbackError) {
        console.error('备选SOP模板请求也失败:', fallbackError);
        throw error; // 重新抛出原始错误
      }
    }
  },
  
  triggerSop: async (templateId, targetType, targetId) => {
    const tenantStr = localStorage.getItem('tenant');
    let tenantId;
    
    try {
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        tenantId = tenant.id.toString();
      } else {
        throw new Error('找不到租户信息');
      }
    } catch (error) {
      console.error('解析tenant信息失败:', error);
      throw new Error('租户信息解析失败，无法继续操作');
    }
    
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const userId = currentUser.id || 1;
    
    return await api.post('/v1/sop/trigger', {
      templateId,
      targetType,
      targetId,
      userId
    });
  }
};

// 群组字段映射 - 根据GroupEntity实体类映射字段
const mapGroupFields = (group) => {
  // 优先使用WeworkGroup中的createTime字段
  let createTimeValue = null;
  if (group.weworkGroup && group.weworkGroup.createTime) {
    createTimeValue = group.weworkGroup.createTime;
  } else if (group.createTime) {
    createTimeValue = group.createTime;
  } else if (group.createdAt) {
    createTimeValue = group.createdAt;
  }
  
  // 优先使用WeworkGroup中的ownerName字段
  let ownerNameValue = null;
  if (group.weworkGroup && group.weworkGroup.ownerName) {
    ownerNameValue = group.weworkGroup.ownerName;
  } else if (group.ownerName) {
    ownerNameValue = group.ownerName;
  } else if (group.weworkGroup && group.weworkGroup.owner) {
    ownerNameValue = group.weworkGroup.owner;
  } else if (group.owner) {
    ownerNameValue = group.owner;
  }
  
  return {
    id: group.id,
    name: group.name,
    description: group.description || '',
    groupCode: group.groupCode,
    groupType: group.groupType || group.type, // 确保使用后端实际字段名
    ownerType: group.ownerType,
    ownerId: group.ownerId,
    // 优先使用处理好的ownerName
    ownerName: ownerNameValue || '-',
    status: group.status || 'active',
    source: (group.source || '').toUpperCase(), // 与标签页面保持一致，使用大写
    memberCount: group.memberCount || 0,
    sourceId: group.sourceId, // 企业微信ID对应sourceId字段
    createTime: createTimeValue, // 优先使用createTime字段
    updatedAt: group.updatedAt
  };
};

// 群组成员字段映射
const mapMemberFields = (member) => {
  return {
    id: member.id,
    groupId: member.groupId,
    memberType: member.memberType,
    memberId: member.memberId,
    source: member.source,
    sourceId: member.sourceId,
    role: member.role,
    nickname: member.nickname,
    joinTime: member.joinTime,
    createdAt: member.createdAt,
    updatedAt: member.updatedAt
  };
};

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [ownerFilter, setOwnerFilter] = useState(undefined); // 选择的群主员工ID（仅激活成员）
  const [dateRange, setDateRange] = useState(null); // [startDayjs, endDayjs]
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [currentMembers, setCurrentMembers] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [memberLoading, setMemberLoading] = useState(false);
  const { tenant } = useAuth(); // 从认证上下文获取当前租户信息
  const [sopTemplates, setSopTemplates] = useState([]); // 存储SOP模板列表
  const [sopModalVisible, setSopModalVisible] = useState(false); // SOP模态框可见性
  const [selectedGroupId, setSelectedGroupId] = useState(null); // 选中的群组ID
  const [triggerLoading, setTriggerLoading] = useState(false); // 触发SOP的加载状态
  const [selectedTemplateId, setSelectedTemplateId] = useState(null); // 选中的模板ID
  // 新增状态：SOP执行记录抽屉
  const [sopRecordDrawerVisible, setSopRecordDrawerVisible] = useState(false);
  const [sopRecordGroupId, setSopRecordGroupId] = useState(null);
  
  // 新增分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `共 ${total} 条记录`,
  });

  // 多选导出相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowMap, setSelectedRowMap] = useState({}); // id -> record

  // 加载群组列表
  const fetchGroups = async (page = 1, pageSize = 10, searchKey = null) => {
    try {
      setLoading(true);
      
      // 准备分页参数并调用API
      const params = {
        page: page - 1,  // 后端从0开始，前端从1开始，需要转换
        size: pageSize,
        name: searchKey ? searchKey : undefined, // 如果有搜索文本就传递
        ownerId: ownerFilter ? ownerFilter : undefined,
        startTime: (dateRange && dateRange[0]) ? dayjs(dateRange[0]).startOf('day').toDate().toISOString() : undefined,
        endTime: (dateRange && dateRange[1]) ? dayjs(dateRange[1]).endOf('day').toDate().toISOString() : undefined
      };
      
      const data = await groupService.getGroups(params);
      
      // 将原始数据映射为前端需要的格式，并按创建时间倒序排序
      const mappedGroups = (data.content || []).map(mapGroupFields);
      const sortedGroups = [...mappedGroups].sort((a, b) => {
        const aTime = a.createTime ? new Date(a.createTime).getTime() : 0;
        const bTime = b.createTime ? new Date(b.createTime).getTime() : 0;
        return bTime - aTime;
      });
      setGroups(sortedGroups);
      
      // 更新分页状态
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize: pageSize,
        total: data.totalElements || 0
      }));
    } catch (error) {
      console.error('获取群组列表失败:', error);
      message.error('获取群组列表失败');
    } finally {
      setLoading(false);
    }
  };
  // 根据筛选条件对当前页数据进行本地过滤
  const getFilteredGroups = () => {
    let result = groups;
    if (ownerFilter) {
      result = result.filter(g => String(g.ownerId) === String(ownerFilter));
    }
    if (dateRange && Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
      const start = dateRange[0].startOf('day');
      const end = dateRange[1].endOf('day');
      result = result.filter(g => {
        const ct = g.createTime ? dayjs(g.createTime) : null;
        return ct && ct.isAfter(start.subtract(1, 'millisecond')) && ct.isBefore(end.add(1, 'millisecond'));
      });
    }
    return result;
  };

  // 处理导出选中项为CSV
  const handleExportSelected = () => {
    const selectedItems = Object.values(selectedRowMap);
    if (!selectedItems || selectedItems.length === 0) {
      message.warning('请先选择要导出的群');
      return;
    }
    const headers = ['群聊名称', '群人数', '群主', '创建时间'];
    const lines = [headers.join(',')];
    selectedItems.forEach(item => {
      const row = [
        item.name || '',
        item.memberCount != null ? String(item.memberCount) : '',
        item.ownerName || '',
        item.createTime ? new Date(item.createTime).toLocaleString() : ''
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
      lines.push(row);
    });
    const csvContent = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `群组导出_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // 重置筛选
  const handleResetFilters = () => {
    setOwnerFilter(undefined);
    setDateRange(null);
    setSearchText('');
    setSelectedRowKeys([]);
    setSelectedRowMap({});
    fetchGroups(1, pagination.pageSize, null);
  };

  // Table 多选配置，保持跨页勾选
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys, newSelectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedRowMap(prev => {
        const next = { ...prev };
        // 先移除当前页未被选中的项
        groups.forEach(g => {
          if (!newSelectedRowKeys.includes(g.id)) {
            delete next[g.id];
          }
        });
        // 添加当前变更中选中的项
        newSelectedRows.forEach(r => {
          next[r.id] = r;
        });
        return next;
      });
    }
  };


  useEffect(() => {
    fetchGroups();
  }, []);

  // 当群主或时间范围变更时，自动触发检索
  const filterChangeRef = useRef(false);
  useEffect(() => {
    if (!filterChangeRef.current) {
      // 跳过首次执行，避免与初始加载重复
      filterChangeRef.current = true;
      return;
    }
    fetchGroups(1, pagination.pageSize, searchText && searchText.trim() ? searchText : null);
  }, [ownerFilter, dateRange]);

  // 同步企业微信群组
  const handleSync = async () => {
    // 如果已经在同步中，不要重复触发
    if (syncLoading) {
      message.info('同步任务正在进行中，请稍候...');
      return;
    }
    
    try {
      setSyncLoading(true);
      console.log('开始同步企业微信群组...');
      const response = await localGroupService.syncGroups();
      console.log('同步响应:', response);
      
      // 处理响应数据
      if (response && response.data) {
        message.success('群组同步任务已提交，将在后台进行处理');
      } else {
        message.success('群组同步任务已提交');
      }
      
      // 延迟5秒后重新加载数据，给服务器一些处理时间
      setTimeout(() => {
        fetchGroups(pagination.current, pagination.pageSize, searchText || null);
        message.info('正在刷新群组列表，部分同步结果可能需要稍后才能显示');
      }, 5000);
    } catch (error) {
      console.error('同步群组失败:', error);
      if (error.response) {
        console.error('错误响应:', error.response.data);
        message.error(`同步群组失败: ${error.response.data.message || '服务器错误'}`);
      } else if (error.request) {
        console.error('请求未收到响应');
        message.error('同步群组失败: 服务器无响应');
      } else {
        console.error('请求配置错误:', error.message);
        message.error(`同步群组失败: ${error.message}`);
      }
    } finally {
      setSyncLoading(false);
    }
  };

  // 查看群组详情
  const showGroupDetail = async (groupId) => {
    try {
      setDetailVisible(true);
      setDetailLoading(true);
      
      // 获取群组详情
      const response = await groupService.getGroupById(groupId);
      console.log('群组详情响应:', response);
      
      // 提取group对象并映射字段
      const group = response.group || response;
      setCurrentGroup(mapGroupFields(group));
      
      // 加载群组成员（包括未知成员）
      setMemberLoading(true);
      const membersResponse = await groupService.getGroupMembersWithUnknown(groupId);
      console.log('群组成员响应:', membersResponse);
      
      // 处理新的混合成员数据结构 
      // 后端返回格式为: GroupMemberDTO[]
      let membersArray = [];
      
      if (membersResponse) {
        if (Array.isArray(membersResponse)) {
          // 如果直接返回数组（新的DTO格式）
          membersArray = membersResponse;
        } else if (membersResponse.members && Array.isArray(membersResponse.members)) {
          // 兼容旧格式
          membersArray = membersResponse.members;
        } else {
          console.warn('成员数据结构不符合预期:', membersResponse);
        }
      }
      
      // 新的DTO直接使用，不需要映射转换
      console.log('群组成员数据（包含未知成员）:', membersArray);
      setCurrentMembers(membersArray);
    } catch (error) {
      console.error('获取群组详情失败:', error);
      message.error('获取群组详情失败');
    } finally {
      setDetailLoading(false);
      setMemberLoading(false);
    }
  };

  // 关闭详情模态框
  const handleDetailClose = () => {
    setDetailVisible(false);
  };

  // 获取SOP模板列表
  const fetchSopTemplates = async () => {
    try {
      setTriggerLoading(true);
      const templates = await localGroupService.getSopTemplates();
      
      // 根据返回数据结构提取模板列表
      let templateList = [];
      if (Array.isArray(templates)) {
        templateList = templates;
      } else if (templates && Array.isArray(templates.content)) {
        templateList = templates.content;
      } else if (templates && typeof templates === 'object') {
        templateList = [templates]; // 单个对象转为数组
      }
      
      // 过滤掉已删除或非活跃的模板，同时确保只有GROUP类型的模板
      const filteredTemplates = templateList.filter(template => 
        !template.deleted && 
        template.status !== 'inactive' && 
        template.targetType === 'GROUP'
      );
      
      console.log('获取到客户群推送:', filteredTemplates);
      setSopTemplates(filteredTemplates);
    } catch (error) {
      console.error('获取推送列表失败:', error);
      message.error('获取推送列表失败: ' + (error.response?.data?.message || error.message || '未知错误'));
      setSopTemplates([]);
    } finally {
      setTriggerLoading(false);
    }
  };

  // 显示触发SOP的模态框
  const showTriggerSopModal = async (groupId) => {
    setSelectedGroupId(groupId);
    setSopModalVisible(true);
    fetchSopTemplates();

  };

  // 关闭SOP模态框
  const handleSopModalClose = () => {
    setSopModalVisible(false);
    setSelectedTemplateId(null);
  };

  // 触发SOP
  const handleTriggerSop = async () => {
    if (!selectedTemplateId) {
      message.warning('请选择一个推送');
      return;
    }

    try {
      setTriggerLoading(true);
      const response = await localGroupService.triggerSop(
        selectedTemplateId,
        'GROUP',
        selectedGroupId
      );
      
      message.success('成功触发SOP');
      setSopModalVisible(false);
      setSelectedTemplateId(null);
    } catch (error) {
      console.error('触发SOP失败:', error);
      message.error('触发SOP失败: ' + (error.response?.data?.message || error.message || '未知错误'));
    } finally {
      setTriggerLoading(false);
    }
  };

  // 新增函数：显示群组SOP执行记录
  const showSopExecutionRecords = (groupId, e) => {
    if (e) {
      e.stopPropagation(); // 阻止事件冒泡
    }
    setSopRecordGroupId(groupId);
    setSopRecordDrawerVisible(true);
  };

  // 新增函数：关闭SOP执行记录抽屉
  const handleSopRecordDrawerClose = () => {
    setSopRecordDrawerVisible(false);
  };

  // 新增：处理分页变化
  const handleTableChange = (paginationConfig) => {
    fetchGroups(paginationConfig.current, paginationConfig.pageSize, searchText || null);
  };

  // 新增：处理搜索
  const handleSearch = (value) => {
    setSearchText(value);
    // 搜索时重置到第一页
    fetchGroups(1, pagination.pageSize, value || null);
  };

  // 表格列定义
  const columns = [
    {
      title: '群组名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <TeamOutlined /> 
          <span>{text}</span>
        </Space>
      )
    },
    {
      title: '群人数',
      dataIndex: 'memberCount',
      key: 'memberCount',
      width: 100,
      render: (num) => (typeof num === 'number' ? num : '-')
    },
    {
      title: '群主',
      dataIndex: 'ownerName',
      key: 'ownerName',
      render: (text) => text || '-',
      width: 150,
    },
    // {
    //   title: '描述',
    //   dataIndex: 'description',
    //   key: 'description',
    //   ellipsis: true,
    //   width: 150,
    // },
    // {
    //   title: '来源',
    //   dataIndex: 'source',
    //   key: 'source',
    //   width: 100,
    //   render: (source) => {
    //     let color = 'default';
    //     let text = '系统';
        
    //     if (source === 'WEWORK') {
    //       color = 'blue';
    //       text = '企业微信';
    //     }
        
    //     return <Tag color={color}>{text}</Tag>;
    //   }
    // },
    // {
    //   title: '状态',
    //   dataIndex: 'status',
    //   key: 'status',
    //   width: 100,
    //   render: (status) => {
    //     let color = 'default';
    //     let text = '未知';
        
    //     switch(status) {
    //       case 'active':
    //         color = 'success';
    //         text = '活跃';
    //         break;
    //       case 'inactive':
    //         color = 'default';
    //         text = '不活跃';
    //         break;
    //       case 'archived':
    //         color = 'warning';
    //         text = '已归档';
    //         break;
    //       default:
    //         break;
    //     }
        
    //     return <Tag color={color}>{text}</Tag>;
    //   }
    // },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (text) => text ? new Date(text).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            onClick={() => showGroupDetail(record.id)}
          >
            详情
          </Button>
          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation(); // 阻止事件冒泡
              showTriggerSopModal(record.id);
            }}
          >
            触发
          </Button>
          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation(); // 阻止事件冒泡
              showSopExecutionRecords(record.id, e);
            }}
          >
            执行记录
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>社群管理</Title>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <Input.Search
                  placeholder="搜索群组"
                  enterButton
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onPressEnter={e => handleSearch(e.target.value)}
                  onSearch={(val) => handleSearch(val)}
                  style={{ width: 200 }}
                  allowClear
                />
              </Space>
            </Col>
            <Col>
              <Space>
                <EmployeeSelect
                  multiple={false}
                  placeholder="筛选群主（仅激活成员）"
                  value={ownerFilter}
                  onChange={setOwnerFilter}
                  allowInactive={false}
                  width="220px"
                  showDisplayModeSwitch={false}
                  defaultDisplayMode="department"
                />
              </Space>
            </Col>
            <Col>
              <Space>
                <DatePicker.RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  disabledDate={() => false}
                  allowEmpty={[true, true]}
                />
              </Space>
            </Col>
            <Col>
              <Space>
                <Button onClick={handleResetFilters}>重置筛选</Button>
              </Space>
            </Col>
          </Row>
          <Row>
            <Col>
              <Space>
                <Button 
                  type="primary" 
                  icon={<SyncOutlined spin={syncLoading} />} 
                  onClick={handleSync}
                  loading={syncLoading}
                >
                  同步企业微信群组
                </Button>
                <Button 
                  type="default"
                  onClick={handleExportSelected}
                  disabled={Object.keys(selectedRowMap).length === 0}
                >
                  导出
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
        <Table
          columns={columns}
          dataSource={groups}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          rowSelection={rowSelection}
          onChange={handleTableChange}
        />
      </Card>

      {/* 群组详情模态框 */}
      <Modal
        title="群组详情"
        open={detailVisible}
        onCancel={handleDetailClose}
        footer={[
          <Button key="close" onClick={handleDetailClose}>
            关闭
          </Button>
        ]}
        width={800}
        destroyOnClose
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: '30px' }}>
            <Spin size="large" />
          </div>
        ) : currentGroup ? (
          <Tabs defaultActiveKey="1">
            <TabPane tab="基本信息" key="1">
              <Descriptions bordered column={1}>
                <Descriptions.Item label="群组名称">
                  <Space>
                    {currentGroup.name}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="群主">{currentGroup.ownerName || '-'}</Descriptions.Item>
                <Descriptions.Item label="描述">{currentGroup.description || '-'}</Descriptions.Item>
                {/* <Descriptions.Item label="群组类型">
                  {currentGroup.groupType ? (
                    <Tag color="blue">{currentGroup.groupType}</Tag>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                  <Tag color={
                    currentGroup.status === 'active' ? 'success' :
                    currentGroup.status === 'inactive' ? 'default' :
                    currentGroup.status === 'archived' ? 'warning' : 'default'
                  }>
                    {
                      currentGroup.status === 'active' ? '活跃' :
                      currentGroup.status === 'inactive' ? '不活跃' :
                      currentGroup.status === 'archived' ? '已归档' : '未知'
                    }
                  </Tag>
                </Descriptions.Item> */}
                <Descriptions.Item label="来源">
                  {currentGroup.source === 'WEWORK' ? '企业微信' : '系统'}
                </Descriptions.Item>
                <Descriptions.Item label="创建时间">
                  {currentGroup.createTime ? new Date(currentGroup.createTime).toLocaleString() : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="更新时间">
                  {currentGroup.updatedAt ? new Date(currentGroup.updatedAt).toLocaleString() : '-'}
                </Descriptions.Item>
              </Descriptions>
              
              {currentGroup.source === 'WEWORK' && (
                <Divider>企业微信信息</Divider>
              )}
            </TabPane>
            
            <TabPane tab={`成员列表 (${currentMembers.length})`} key="2">
              {memberLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin />
                </div>
              ) : currentMembers.length > 0 ? (
                <List
                  dataSource={currentMembers}
                  renderItem={member => (
                    <List.Item key={member.memberId || member.weworkUserId || Math.random()}>
                      <List.Item.Meta
                        // avatar={
                        //   <Avatar 
                        //     src={member.avatar}
                        //     icon={<UserOutlined />}
                        //     style={{
                        //       backgroundColor: member.isKnownCustomer ? '#1890ff' : '#f0f0f0',
                        //       color: member.isKnownCustomer ? '#fff' : '#999'
                        //     }}
                        //   />
                        // }
                        title={
                          <Space>
                            <span>{member.name || member.groupNickname || '客户'}</span>
                            {!member.isKnownCustomer && (
                              <Tag color="orange">未添加客户</Tag>
                            )}
                          </Space>
                        }
                        description={
                          <Space direction="vertical" size={0}>
                            <Text type="secondary">
                              成员类型: {member.memberType === 'CUSTOMER' ? '客户' : member.memberType === 'EMPLOYEE' ? '企微成员' : '-'}
                            </Text>
                            {member.joinTime && (
                              <Text type="secondary">
                                加入时间: {new Date(member.joinTime).toLocaleString()}
                              </Text>
                            )}
                            {/* {member.invitor && (
                              <Text type="secondary">
                                邀请人: {member.invitor}
                              </Text>
                            )} */}
                            {/* {!member.isKnownCustomer && member.weworkUserId && (
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                企微ID: {member.weworkUserId}
                              </Text>
                            )} */}
                          </Space>
                        }
                      />
                      {!member.isKnownCustomer && (
                        <Tooltip title="该成员尚未在客户系统中创建档案">
                          <InfoCircleOutlined style={{ color: '#ff9800', marginLeft: 8 }} />
                        </Tooltip>
                      )}
                    </List.Item>
                  )}
                  pagination={
                    currentMembers.length > 10 ? {
                      pageSize: 10,
                      showTotal: total => `共 ${total} 位成员`
                    } : false
                  }
                />
              ) : (
                <Empty description="暂无成员" />
              )}
            </TabPane>

            <TabPane tab="推送记录" key="3">
              {currentGroup && (
                <SopExecutionRecords 
                  targetId={currentGroup.id} 
                  targetType="GROUP"
                  title="当前群组的推送记录"
                />
              )}
            </TabPane>
          </Tabs>
        ) : (
          <Empty description="未找到群组信息" />
        )}
      </Modal>

      {/* 添加SOP模板选择模态框 */}
      <Modal
        title="选择推送"
        open={sopModalVisible}
        onCancel={handleSopModalClose}
        footer={[
          <Button key="cancel" onClick={handleSopModalClose}>
            取消
          </Button>,
          <Button 
            key="trigger" 
            type="primary" 
            loading={triggerLoading}
            onClick={handleTriggerSop}
          >
            触发
          </Button>
        ]}
      >
        <Spin spinning={triggerLoading}>
          <div style={{ marginBottom: 16 }}>
            <Text>请选择要触发的推送：</Text>
          </div>
          
          {sopTemplates.length > 0 ? (
            <List
              bordered
              dataSource={sopTemplates}
              renderItem={template => (
                <List.Item
                  onClick={() => setSelectedTemplateId(template.id)}
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: selectedTemplateId === template.id ? '#f0f5ff' : 'transparent'
                  }}
                >
                  <Space>
                    <Radio checked={selectedTemplateId === template.id} />
                    <div>
                      <Text strong>{template.name}</Text>
                      {template.description && (
                        <div>
                          <Text type="secondary">{template.description}</Text>
                        </div>
                      )}
                    </div>
                  </Space>
                </List.Item>
              )}
            />
          ) : (
            <Empty description="暂无可用的推送" />
          )}
        </Spin>
      </Modal>

      {/* 新增：SOP执行记录抽屉 */}
      <Drawer
        title="推送执行记录"
        width={700}
        open={sopRecordDrawerVisible}
        onClose={handleSopRecordDrawerClose}
        destroyOnClose
      >
        {sopRecordGroupId && (
          <SopExecutionRecords 
            targetId={sopRecordGroupId} 
            targetType="GROUP"
            title="群组推送执行记录"
          />
        )}
      </Drawer>
    </div>
  );
};

export default Groups; 